import type { App } from 'vue'
import { createI18n } from 'vue-i18n'
import { useLocaleStoreWithOut } from '@/store/modules/locale'
import type { I18n, I18nOptions } from 'vue-i18n'
import { setHtmlPageLang } from './helper'
import { getMultiLangList } from '@/api/com/com001'
import { useStorage } from '@/hooks/web/useStorage'

export let i18n: ReturnType<typeof createI18n>

const createI18nOptions = async (): Promise<I18nOptions> => {
  const { setStorage, getStorage } = useStorage('localStorage')

  try {
    const localeStore = useLocaleStoreWithOut()
    const locale = localeStore.getCurrentLocale
    const localeMap = localeStore.getLocaleMap
    let defaultLocal = getStorage(locale.lang)

    if (!defaultLocal) {
      const res = await getMultiLangList(locale.lang)

      if (res) {
        defaultLocal = res.data
        setStorage(locale.lang, res.data)
      }
    }

    setHtmlPageLang(locale.lang)

    localeStore.setCurrentLocale({
      lang: locale.lang
      // elLocale: elLocal
    })

    return {
      legacy: false,
      locale: locale.lang,
      fallbackLocale: locale.lang,
      messages: {
        [locale.lang]: defaultLocal
      },
      availableLocales: localeMap.map((v) => v.lang),
      sync: true,
      silentTranslationWarn: true,
      missingWarn: false,
      silentFallbackWarn: true
    }
  } catch (error) {
    console.error('Error while creating i18n options:', error)
    // Handle error or re-throw
    throw error
  }
}

export const setupI18n = async (app: App<Element>) => {
  const options = await createI18nOptions()
  i18n = createI18n(options) as I18n
  app.use(i18n)
}

export const clearLocaleCache = (lang?: string) => {
  const { removeStorage } = useStorage('localStorage')
  const localeStore = useLocaleStoreWithOut()

  const currentLocale = localeStore.getCurrentLocale
  const localeMap = localeStore.getLocaleMap

  const targetLocale = (lang ? localeMap.find((v) => v.lang === lang) : undefined) || currentLocale

  removeStorage(targetLocale.lang)
}

export const reloadI18nForLang = async (lang?: string) => {
  const { setStorage } = useStorage('localStorage')
  const localeStore = useLocaleStoreWithOut()

  const currentLocale = localeStore.getCurrentLocale
  const localeMap = localeStore.getLocaleMap

  const targetLocale = (lang ? localeMap.find((v) => v.lang === lang) : undefined) || currentLocale

  const targetLang = targetLocale.lang

  const res = await getMultiLangList(targetLang)
  const messages = res.data

  setStorage(targetLang, messages)

  localeStore.setCurrentLocale(targetLocale)
  setHtmlPageLang(targetLang)

  if (i18n) {
    i18n.global.setLocaleMessage(targetLang, messages)
    ;(i18n.global.locale as any).value = targetLang
    ;(i18n.global.fallbackLocale as any) = targetLang
  } else {
    console.warn('i18n is not initialized yet')
  }
}
